import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, X } from "lucide-react";

// Signup gate: shown the moment a logged out visitor tries to swipe.
// Browsing stays free; liking and passing require an account.
export default function SignupGate({ open, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Create an account to start swiping"
            className="relative w-full max-w-sm rounded-2xl border border-night-line bg-night-surface p-6 pt-8 text-center shadow-ambient"
          >
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="absolute right-3 top-3 rounded-full p-2 text-night-sub transition hover:bg-night-tint hover:text-night-text"
            >
              <X size={18} />
            </button>

            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-night-tint text-brand-primary">
              <Heart size={22} strokeWidth={2} />
            </span>
            <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-night-text">
              Make it yours
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-night-sub">
              Create a free account to start swiping. Your likes and matches are saved to your profile.
            </p>

            <button
              type="button"
              onClick={() => navigate("/auth/signup")}
              className="mt-5 w-full rounded-full bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-primary-hover"
            >
              Sign up free
            </button>
            <button
              type="button"
              onClick={() => navigate("/auth/login")}
              className="mt-2 w-full rounded-full px-4 py-2.5 text-sm font-semibold text-night-sub transition hover:text-night-text"
            >
              Log in
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
